import React, { useState, useEffect, useCallback } from 'react';
import { TextField } from '@mui/material';

// Debounce-Funktion
const debounce = (func: Function, delay: number) => {
  let timeoutId: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

interface LocalTextbelegeFieldProps {
  label: string;
  initialValue: string;
  belegIndex: number;
  field: string;
  disabled: boolean;
  multiline?: boolean;
  rows?: number;
  sx?: any;
  onFieldChange: (belegIndex: number, field: string, value: string) => void;
}

// Optimiertes Textbelege-Feld mit lokalem State
const LocalTextbelegeField = React.memo<LocalTextbelegeFieldProps>(({ 
  label, 
  initialValue, 
  belegIndex, 
  field, 
  disabled, 
  multiline = false, 
  rows = 1, 
  sx = {}, 
  onFieldChange 
}) => {
  const [localValue, setLocalValue] = useState(initialValue);
  
  // Update lokaler Wert wenn sich initialValue ändert
  useEffect(() => {
    setLocalValue(initialValue);
  }, [initialValue]);

  // Debounced Change für Parent-Update
  const debouncedChange = useCallback(
    debounce((value: string) => {
      onFieldChange(belegIndex, field, value);
    }, 300), // 300ms für Textbelege
    [belegIndex, field, onFieldChange]
  );

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue); // Sofortiges lokales Update
    debouncedChange(newValue); // Verzögertes Parent-Update
  }, [debouncedChange]);

  return (
    <TextField
      fullWidth
      multiline={multiline}
      {...(multiline ? { minRows: rows } : {})}
      label={label}
      value={localValue}
      disabled={disabled}
      onChange={handleChange}
      sx={{ 
        mb: 1, 
        ...sx, 
        '& .MuiInputBase-input': { overflow: 'hidden', resize: multiline ? 'vertical' : 'none' },
        '& .MuiInputBase-inputMultiline': { overflow: 'hidden', resize: multiline ? 'vertical' : 'none' }
      }}
      inputProps={{ style: { fontSize: '0.875rem' } }}
    />
  );
});

LocalTextbelegeField.displayName = 'LocalTextbelegeField';

export default LocalTextbelegeField;